import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import * as PATHS from 'constants/paths';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getIsSubscriptionLoading, getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { AuthModal } from 'interfaces/auth';
import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { useAuthModalsContext } from 'components/AuthModals/AuthModals';
import { CreateProjectModal } from 'components/CreateProjectModal';
import { useModalContext } from 'components/ModalManager';

import { IHeaderLogicContext, IHeaderLogicProviderProps } from './types';

const headerLogicContext = createContext<IHeaderLogicContext>({
  isUserAuth: false,
  isUpgradeButtonVisible: false,
  isFeeTrialButtonVisible: false,
  handleWorkspaceClick: () => {},
  handleClickCreateProject: () => {},
  handleClickFreeTrial: () => {},
  handleClickUpgradePlan: () => {},
  handleSignInModalOpen: () => {},
  handleSignUpModalOpen: () => {},
  isPayedSubscription: false,
  isStarterSubscription: false,
  hasSubscription: false,
});

export const useHeaderLogic = () => useContext(headerLogicContext);

export const HeaderLogicProvider: FC<IHeaderLogicProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { showModal, hideModal } = useModalContext();
  const { setAuthModal, setIsModalOpened } = useAuthModalsContext();
  const [searchParams] = useSearchParams();
  const pSignUp = searchParams.get('pSignUp');
  const location = useLocation();
  const isSubscriptionPage2 = location.pathname === '/subscription-plans-2';

  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();
  const isSubscriptionLoading = useUserSubscriptionStore(getIsSubscriptionLoading);
  const subscriptionPlan = useUserSubscriptionStore(getUserSubscription)?.currentSubscription.subscriptionPlan;

  const isPayedSubscription = subscriptionPlan !== SUBSCRIPTION_PLAN.FREE;
  const hasSubscription = !!subscriptionPlan;
  const isStarterSubscription = subscriptionPlan === SUBSCRIPTION_PLAN.STARTER;

  const isUpgradeButtonVisible = !isPayedSubscription && !isSubscriptionLoading;


  const handleWorkspaceClick = useCallback(() => {
    navigate(PATHS.WORKSPACE);
  }, []);

  const handleClickCreateProject = useCallback(() => {
    showModal(CreateProjectModal, {
      close: hideModal,
    });
  }, []);

  const handleClickFreeTrial = useCallback(() => {
    navigate(PATHS.SUBSCRIPTION_PLAN);
  }, []);

  const handleClickUpgradePlan = useCallback(() => {
    if (isAuthenticated) {
      navigate(isSubscriptionPage2 ? PATHS.SUBSCRIPTION_PLAN_2 : PATHS.SUBSCRIPTION_PLAN);
      return;
    }
    setAuthModal(AuthModal.LOGIN);
    setIsModalOpened(true);
  }, [isAuthenticated, pSignUp]);

  const handleSignInModalOpen = useCallback((): void => {
    setAuthModal(AuthModal.LOGIN);
    setIsModalOpened(true);
  }, []);

  const handleSignUpModalOpen = useCallback((): void => {
    setAuthModal(AuthModal.SIGN_UP);
    setIsModalOpened(true);
  }, []);

  return (
    <headerLogicContext.Provider
      value={useMemo(
        () => ({
          isUserAuth: isAuthenticated || isAuthConfiguring,
          isUpgradeButtonVisible,
          isFeeTrialButtonVisible: false,
          isPayedSubscription: isPayedSubscription,
          isStarterSubscription,
          handleWorkspaceClick,
          handleClickCreateProject,
          handleClickFreeTrial,
          handleClickUpgradePlan,
          handleSignInModalOpen,
          handleSignUpModalOpen,
          hasSubscription,
        }),
        [
          isAuthenticated,
          isAuthConfiguring,
          isUpgradeButtonVisible,
          isPayedSubscription,
          isStarterSubscription,
          handleWorkspaceClick,
          handleClickCreateProject,
          handleClickFreeTrial,
          handleClickUpgradePlan,
          handleSignInModalOpen,
          handleSignUpModalOpen,
          hasSubscription,
        ],
      )}
    >
      {children}
    </headerLogicContext.Provider>
  );
};
